import { useEffect, useState } from "react";
import { masterApi, apiError } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { Plus, Pencil, Trash2, Eye, EyeOff, Save } from "lucide-react";

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

export default function MasterVendedores() {
  const { unitId, unidades } = useUnit();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await masterApi.get("/frentistas");
      setItems(unitId === "all" ? data : data.filter(f => f.unit_id === unitId));
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [unitId]);

  const toggleAtivo = async (f) => {
    try {
      await masterApi.put(`/frentistas/${f.id}`, { ativo: !f.ativo });
      load();
    } catch (e) { modal.error(apiError(e)); }
  };

  const del = async (id) => {
    const ok = await modal.confirm({
      title: "Desativar vendedor",
      message: "Deseja desativar este vendedor? Ele perderá o acesso ao sistema.",
      confirmText: "Desativar",
      tone: "danger",
    });
    if (!ok) return;
    try { await masterApi.delete(`/frentistas/${id}`); modal.success("Vendedor desativado com sucesso."); load(); }
    catch (e) { modal.error(apiError(e)); }
  };

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-vendedores">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="chip mb-1.5 text-[10px] py-0.5">Equipe</div>
          <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Vendedores</h1>
        </div>
        <button data-testid="novo-frentista-btn" onClick={() => { setEditing(null); setOpen(true); }}
                className="btn-action btn-novo">
          <Plus size={14} /> Novo Vendedor
        </button>
      </div>

      <div className="card-soft rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="text-[10px] uppercase tracking-widest">Nome</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Usuário</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Unidade</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest">Ativo</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((f) => (
              <TableRow key={f.id} data-testid={`frentista-row-${f.id}`}>
                <TableCell className="font-medium">{f.nome}</TableCell>
                <TableCell className="text-neutral-500">{f.username}</TableCell>
                <TableCell className="text-xs">{unidades.find(u => u.id === f.unit_id)?.nome || "—"}</TableCell>
                <TableCell>
                  <Switch data-testid={`frentista-ativo-${f.id}`} checked={f.ativo} onCheckedChange={() => toggleAtivo(f)} />
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <button data-testid={`frentista-edit-${f.id}`} onClick={() => { setEditing(f); setOpen(true); }}
                          className="icon-action icon-action--edit"><Pencil size={14} /></button>
                  <button data-testid={`frentista-del-${f.id}`} onClick={() => del(f.id)}
                          className="icon-action icon-action--danger"><Trash2 size={14} /></button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-neutral-500 py-6">Nenhum vendedor cadastrado.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl border-none p-0">
          <div className="bg-white p-5 rounded-2xl">
            <FrentistaForm key={editing?.id ?? "new"} unidades={unidades} frentista={editing} onDone={() => { setOpen(false); load(); }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FrentistaForm({ unidades, frentista, onDone }) {
  const [nome, setNome] = useState(frentista?.nome || "");
  const [username, setUsername] = useState(frentista?.username || "");
  const [senha, setSenha] = useState("");
  const [unit, setUnit] = useState(frentista?.unit_id || unidades[0]?.id || "");
  const [show, setShow] = useState(false);
  // readOnly até o foco: bloqueia o autofill do gerenciador de senhas (Chrome ignora autocomplete=off)
  const [roUser, setRoUser] = useState(true);
  const [roPass, setRoPass] = useState(true);
  // Edição: senha escondida até clicar. Sem campo type=password renderizado, o Chrome NÃO vê
  // "formulário de login" e não injeta a credencial salva (venda1) no campo Usuário.
  const [pwdOpen, setPwdOpen] = useState(!frentista);

  const submit = async () => {
    if (!nome || !username || !unit) return modal.error("Preencha nome, usuário e unidade");
    if (!frentista && !senha) return modal.error("Senha obrigatória");
    try {
      if (frentista) {
        const body = { nome, username, unit_id: unit };
        if (senha) body.senha = senha;
        await masterApi.put(`/frentistas/${frentista.id}`, body);
      } else {
        await masterApi.post("/frentistas", { nome, username, senha, unit_id: unit });
      }
      modal.success(frentista ? "Atualizado" : "Vendedor criado");
      onDone();
    } catch (e) { modal.error(apiError(e)); }
  };

  return (
    <div className="space-y-3">
      <DialogHeader>
        <DialogTitle className="font-serif-display text-xl">{frentista ? "Editar Vendedor" : "Novo Vendedor"}</DialogTitle>
      </DialogHeader>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Nome</label>
        <input data-testid="vend-nome" value={nome} onChange={(e) => setNome(e.target.value)} className={inputCls + " mt-1"} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Usuário</label>
        <input data-testid="vend-username" value={username} onChange={(e) => setUsername(e.target.value)}
               name="vend_usuario_nofill" autoComplete="off" readOnly={roUser} onFocus={() => setRoUser(false)}
               className={inputCls + " mt-1"} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">{frentista ? "Nova senha (opcional)" : "Senha"}</label>
        {pwdOpen ? (
          <div className="relative mt-1">
            <input data-testid="vend-senha" type={show ? "text" : "password"} value={senha} onChange={(e) => setSenha(e.target.value)}
                   autoComplete="new-password" readOnly={roPass} onFocus={() => setRoPass(false)} className={inputCls + " pr-10"} />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
              {show ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        ) : (
          <button type="button" data-testid="vend-senha-toggle" onClick={() => setPwdOpen(true)}
                  className="mt-1 inline-flex items-center gap-1.5 text-[12px] text-[color:var(--brand-blue)] font-medium hover:underline">
            <Pencil size={12} /> Definir nova senha
          </button>
        )}
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-neutral-500">Unidade</label>
        <select data-testid="vend-unit" value={unit} onChange={(e) => setUnit(e.target.value)} className={inputCls + " mt-1"}>
          {unidades.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
        </select>
      </div>
      <button data-testid="vend-submit" onClick={submit}
              className="btn-action btn-salvar w-full">
        <Save size={15} /> {frentista ? "Salvar" : "Criar Vendedor"}
      </button>
    </div>
  );
}
